<script setup>
// [xleaders] XO-09 — màn quét QR trước đây chỉ sống trong luồng tạo inbox mới, nên inbox đã rớt
// phải xoá đi tạo lại (mất sạch lịch sử hội thoại). Component này mang đúng màn đó vào trang cài
// đặt của inbox đã tồn tại.
import { computed, onBeforeUnmount, onMounted, ref } from 'vue';
import Banner from 'dashboard/components-next/banner/Banner.vue';
import NextButton from 'dashboard/components-next/button/Button.vue';
import ZaloPersonalChannel from 'dashboard/api/channel/zaloPersonalChannel';

const props = defineProps({
  inbox: {
    type: Object,
    default: () => ({}),
  },
  // Chỉ hộp thoại bấm từ dải cảnh báo mới được tự mở QR — ở đó cú bấm đã xảy ra rồi. Trang cài đặt
  // inbox thì KHÔNG: admin vào đó để đổi tên inbox cũng sẽ vô tình biến một channel `disconnected`
  // (bị Zalo kick, cron hydrate cố ý bỏ qua) thành `qr_required`, tức kéo nó trở lại vòng cron thử
  // đăng nhập bằng đúng bộ cookie Zalo vừa kick — ghost session mỗi 2 phút, mãi mãi.
  autoStart: {
    type: Boolean,
    default: false,
  },
});

// Nơi gọi cần biết ngay lúc nối lại được để gỡ dải cảnh báo, thay vì đợi hết một vòng poll 30 giây.
const emit = defineEmits(['reconnected']);

const QR_POLL_INTERVAL_MS = 1500;
const STATUS_POLL_INTERVAL_MS = 15000;
// zca-js 2.1.2 huỷ mã QR ở mốc 100s rồi reject cả lượt `loginQR`; xin mã mới trước mốc đó, nếu
// không sale sẽ nhìn "đang tạo mã QR" trong 20 giây với một phiên đã chết ở sidecar.
const QR_TIMEOUT_MS = 90000;
// Mỗi lần xin mã mới là một lượt `loginQR` thật tới Zalo. Trang này bị bỏ mở qua đêm là chuyện sẽ
// xảy ra, nên phải có điểm dừng thay vì làm mới vô hạn — sau đó bắt sale bấm mới xin tiếp.
const MAX_QR_REFRESHES = 2;
// `zalo_personal_qr_status` trả 422 khi sidecar chết, và sidecar chết là ca RẤT dễ gặp ở đây —
// kênh rớt vì chính sidecar rớt. Không đếm số lần hỏng thì trang đứng ở "đang tạo mã QR" vĩnh viễn.
const MAX_CONSECUTIVE_POLL_FAILURES = 4;
const TERMINAL_STATUSES = ['disconnected', 'qr_required'];

const status = ref(null);
const qrDataUrl = ref(null);
const isScanning = ref(false);
const isStarting = ref(false);
const hasError = ref(false);
const hasExpired = ref(false);
const hasReconnected = ref(false);
let pollHandle = null;
let isUnmounted = false;
let qrStartedAt = 0;
let qrRefreshCount = 0;
let consecutivePollFailures = 0;

const isTerminal = computed(() => TERMINAL_STATUSES.includes(status.value));
const isReconnecting = computed(() => status.value === 'reconnecting');

const stopPolling = () => {
  if (pollHandle) clearInterval(pollHandle);
  pollHandle = null;
};

const stopScanning = () => {
  stopPolling();
  isScanning.value = false;
  qrDataUrl.value = null;
};

// Vòng chậm: giữ cho panel nói đúng sự thật khi trang bị mở lâu. KHÔNG tự mở QR từ vòng này —
// người dùng phải bấm, vì mở QR là một hành động thật tới Zalo chứ không phải một lượt đọc.
const pollStatus = async () => {
  try {
    const { data } = await ZaloPersonalChannel.connectionStatus(props.inbox.id);
    const previous = status.value;
    status.value = data.status;
    if (data.status !== 'connected') return;
    // Kênh tự lành (cron hydrate khôi phục được bằng cookie, hoặc sale nối lại từ điện thoại).
    // Phải xoá mọi ngõ cụt còn treo: nút "xin mã QR mới" nằm lại trên một phiên đang khoẻ, bấm vào
    // là `destroySession` giết đúng cái phiên đang gửi tin được.
    hasError.value = false;
    hasExpired.value = false;
    // `previous === null` là lượt đọc đầu tiên lúc mount — kênh vốn đang khoẻ, không phải "vừa nối
    // lại", đừng báo mừng.
    if (previous === null) return;
    stopScanning();
    hasReconnected.value = true;
    emit('reconnected');
  } catch (error) {
    // Lỗi nhất thời: giữ nguyên thứ đang hiện.
  }
};

const startStatusPolling = () => {
  stopPolling();
  if (isUnmounted) return;
  pollHandle = setInterval(pollStatus, STATUS_POLL_INTERVAL_MS);
};

const pollQr = async () => {
  try {
    const { data } = await ZaloPersonalChannel.qrStatus(props.inbox.id);
    consecutivePollFailures = 0;
    qrDataUrl.value = data.qr_data_url;
    status.value = data.status;
    if (data.status === 'connected') {
      stopScanning();
      hasReconnected.value = true;
      emit('reconnected');
      return;
    }
    // Kênh tự lành trong lúc QR còn trên màn hình (cron hydrate khôi phục được bằng cookie cũ):
    // gấp màn QR lại, đừng để sale quét một mã đã vô nghĩa.
    if (!isTerminal.value) {
      stopScanning();
      startStatusPolling();
      return;
    }
    // Mã QR của Zalo hết hạn sau ~2 phút; xin mã mới thay vì để sale nhìn ảnh chết.
    if (Date.now() - qrStartedAt > QR_TIMEOUT_MS) {
      if (qrRefreshCount >= MAX_QR_REFRESHES) {
        stopScanning();
        hasExpired.value = true;
        startStatusPolling();
        return;
      }
      qrRefreshCount += 1;
      await ZaloPersonalChannel.restartQr(props.inbox.id);
      qrStartedAt = Date.now();
    }
  } catch (error) {
    consecutivePollFailures += 1;
    if (consecutivePollFailures >= MAX_CONSECUTIVE_POLL_FAILURES) {
      stopScanning();
      hasError.value = true;
    }
  }
};

const startQr = async () => {
  isStarting.value = true;
  hasError.value = false;
  hasExpired.value = false;
  try {
    await ZaloPersonalChannel.restartQr(props.inbox.id);
    qrStartedAt = Date.now();
    qrRefreshCount = 0;
    consecutivePollFailures = 0;
    stopPolling();
    // Đóng hộp thoại giữa hai round-trip ở trên là chuyện thường (bấm Esc ngay sau khi mở). Không
    // gác thì interval được cắm lên một component đã chết: nó sống hết phiên SPA, gõ sidecar mỗi
    // 1.5s và tới mốc làm mới còn `destroySession` một phiên mà không ai nhìn thấy màn QR nào.
    if (isUnmounted) return;
    isScanning.value = true;
    pollHandle = setInterval(pollQr, QR_POLL_INTERVAL_MS);
    pollQr();
  } catch (error) {
    hasError.value = true;
  } finally {
    isStarting.value = false;
  }
};

onMounted(async () => {
  await pollStatus();
  if (isUnmounted) return;
  // Chỉ TỰ mở QR khi nơi gọi cho phép (xem prop `autoStart`) và chỉ ở trạng thái terminal — sale
  // bấm "Quét lại QR" ở dải cảnh báo rồi mới tới đây, bắt bấm lại lần nữa là thừa. Lúc
  // `reconnecting` thì KHÔNG mở:
  // `start_qr` gọi `destroySession` trong khi `reconnectLoop` vẫn đang chạy, hai luồng đăng nhập
  // đua nhau đúng là thứ khiến Zalo kick (code 3003).
  if (props.autoStart && isTerminal.value) {
    startQr();
    return;
  }
  startStatusPolling();
});

onBeforeUnmount(() => {
  isUnmounted = true;
  stopPolling();
});
</script>

<template>
  <div v-if="hasReconnected">
    <Banner color="teal">
      {{ $t('INBOX_MGMT.ZALO_PERSONAL_RECONNECT.RECONNECTED') }}
    </Banner>
  </div>
  <div v-else-if="isScanning" class="flex flex-col gap-4">
    <Banner color="ruby">
      {{ $t('INBOX_MGMT.ZALO_PERSONAL_RECONNECT.DISCONNECTED') }}
    </Banner>
    <div class="flex flex-col items-center gap-3">
      <p class="text-sm text-n-slate-11 m-0">
        {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_INSTRUCTION') }}
      </p>
      <img
        v-if="qrDataUrl"
        :src="qrDataUrl"
        :alt="$t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_ALT')"
        class="w-64 h-64 border border-n-weak rounded"
      />
      <p v-else class="text-sm text-n-slate-11 m-0">
        {{ $t('INBOX_MGMT.ADD.ZALO_PERSONAL.QR_LOADING') }}
      </p>
    </div>
  </div>
  <div v-else-if="hasError" class="flex flex-col gap-3 items-start">
    <Banner color="ruby">
      {{ $t('INBOX_MGMT.ZALO_PERSONAL_RECONNECT.ERROR') }}
    </Banner>
    <NextButton
      :is-loading="isStarting"
      :label="$t('INBOX_MGMT.ZALO_PERSONAL_RECONNECT.NEW_QR')"
      @click="startQr"
    />
  </div>
  <div v-else-if="hasExpired" class="flex flex-col gap-3 items-start">
    <Banner color="amber">
      {{ $t('INBOX_MGMT.ZALO_PERSONAL_RECONNECT.EXPIRED') }}
    </Banner>
    <NextButton
      :is-loading="isStarting"
      :label="$t('INBOX_MGMT.ZALO_PERSONAL_RECONNECT.NEW_QR')"
      @click="startQr"
    />
  </div>
  <div v-else-if="isTerminal" class="flex flex-col gap-3 items-start">
    <Banner color="ruby">
      {{ $t('INBOX_MGMT.ZALO_PERSONAL_RECONNECT.DISCONNECTED') }}
    </Banner>
    <NextButton
      :is-loading="isStarting"
      :label="$t('INBOX_MGMT.ZALO_PERSONAL_RECONNECT.RESCAN')"
      @click="startQr"
    />
  </div>
  <div v-else-if="isReconnecting">
    <Banner color="amber">
      {{ $t('INBOX_MGMT.ZALO_PERSONAL_RECONNECT.RECONNECTING') }}
    </Banner>
  </div>
</template>
